import React, {
	useState,
	useEffect,
	useCallback,
	useRef,
	useMemo,
} from 'react';
import {
	Box,
	Button,
	IconButton,
	TextField,
	Typography,
	InputAdornment,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Alert,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { makeApiRequest } from '../../Shared/Api';
import { useSnackbar } from 'notistack';
import useConfirm from '../useConfirm';
import {
	draftLineItemId,
	formatLineItemCurrency,
	isDraftLineItem,
	toNumber,
} from '../../Shared/lineItems';

const costTotal = (row) =>
	toNumber(row.quantity, 0) * toNumber(row.unit_cost, 0);

const isCostComplete = (row) =>
	toNumber(row.quantity, 0) > 0 && toNumber(row.unit_cost, 0) > 0;

const costSnapshot = (row) =>
	JSON.stringify({
		description: (row.description || '').trim(),
		quantity: toNumber(row.quantity, 1),
		unit_cost: toNumber(row.unit_cost, 0).toFixed(2),
	});

const createDraftCost = () => {
	const key = draftLineItemId();
	return {
		_key: key,
		id: key,
		description: '',
		quantity: 1,
		unit_cost: '',
	};
};

const JobCostsEditor = ({ jobId }) => {
	const [rows, setRows] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [fetchError, setFetchError] = useState(null);
	const [savingIds, setSavingIds] = useState(() => new Set());
	const lastSavedRef = useRef({});
	const rowsRef = useRef([]);
	const { enqueueSnackbar } = useSnackbar();
	const { confirm, confirmDialog } = useConfirm();

	const fetchCosts = useCallback(async () => {
		if (!jobId) return;
		try {
			setIsLoading(true);
			setFetchError(null);
			const response = await makeApiRequest('GET', `/jobs/${jobId}/costs/`);
			const items = (response || []).map((item) => ({
				...item,
				_key: item.id,
			}));
			const snapshots = {};
			items.forEach((item) => {
				snapshots[item.id] = costSnapshot(item);
			});
			lastSavedRef.current = snapshots;
			setRows(items);
			rowsRef.current = items;
		} catch (error) {
			const message = error.message || 'Failed to load costs';
			setFetchError(message);
			enqueueSnackbar(message, { variant: 'error' });
		} finally {
			setIsLoading(false);
		}
	}, [jobId, enqueueSnackbar]);

	useEffect(() => {
		fetchCosts();
	}, [fetchCosts]);

	useEffect(() => {
		rowsRef.current = rows;
	}, [rows]);

	const setSaving = (rowKey, saving) => {
		setSavingIds((prev) => {
			const next = new Set(prev);
			if (saving) next.add(rowKey);
			else next.delete(rowKey);
			return next;
		});
	};

	const updateRow = (rowKey, patch) => {
		setRows((prev) => {
			const next = prev.map((row) =>
				row._key === rowKey ? { ...row, ...patch } : row,
			);
			rowsRef.current = next;
			return next;
		});
	};

	const saveRow = async (rowKey, { quiet = false } = {}) => {
		const row = rowsRef.current.find((item) => item._key === rowKey);
		if (!row || savingIds.has(rowKey) || !isCostComplete(row)) {
			return false;
		}
		if (
			!isDraftLineItem(row) &&
			costSnapshot(row) === lastSavedRef.current[row.id]
		) {
			return true;
		}

		const payload = {
			description: (row.description || '').trim(),
			quantity: Math.round(toNumber(row.quantity, 1)),
			unit_cost: toNumber(row.unit_cost, 0).toFixed(2),
		};
		const isNew = isDraftLineItem(row);
		const method = isNew ? 'POST' : 'PUT';
		const url = isNew
			? `/jobs/${jobId}/costs/`
			: `/jobs/${jobId}/costs/${row.id}/`;

		setSaving(rowKey, true);
		try {
			const saved = await makeApiRequest(method, url, payload);
			lastSavedRef.current[saved.id] = costSnapshot(saved);
			setRows((prev) => {
				const next = prev.map((item) =>
					item._key === rowKey ? { ...saved, _key: saved.id } : item,
				);
				rowsRef.current = next;
				return next;
			});
			if (!quiet) {
				enqueueSnackbar(isNew ? 'Cost added' : 'Cost saved', {
					variant: 'success',
				});
			}
			return true;
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to save cost', {
				variant: 'error',
			});
			return false;
		} finally {
			setSaving(rowKey, false);
		}
	};

	const handleDeleteRow = async (row) => {
		if (isDraftLineItem(row)) {
			setRows((prev) => {
				const next = prev.filter((item) => item._key !== row._key);
				rowsRef.current = next;
				return next;
			});
			return;
		}
		const confirmed = await confirm('Delete this cost?', {
			title: 'Delete Cost',
			confirmText: 'Delete',
		});
		if (!confirmed) return;
		try {
			await makeApiRequest('DELETE', `/jobs/${jobId}/costs/${row.id}/`);
			delete lastSavedRef.current[row.id];
			setRows((prev) => {
				const next = prev.filter((item) => item._key !== row._key);
				rowsRef.current = next;
				return next;
			});
			enqueueSnackbar('Cost deleted', { variant: 'success' });
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to delete cost', {
				variant: 'error',
			});
		}
	};

	const totalAmount = useMemo(
		() => rows.reduce((sum, row) => sum + costTotal(row), 0),
		[rows],
	);

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
				<CircularProgress size={28} />
			</Box>
		);
	}

	if (fetchError) {
		return (
			<Alert
				severity="error"
				action={
					<Button color="inherit" size="small" onClick={fetchCosts}>
						Retry
					</Button>
				}
			>
				{fetchError}
			</Alert>
		);
	}

	return (
		<Box sx={{ width: '100%' }}>
			{confirmDialog}
			<TableContainer
				sx={{
					border: '1px solid',
					borderColor: 'divider',
					borderRadius: 2,
					bgcolor: 'background.paper',
				}}
			>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700, width: 88 }}>
								Qty
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700, width: 120 }}>
								Unit cost
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700, width: 100 }}>
								Total
							</TableCell>
							<TableCell align="center" sx={{ width: 52 }} />
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.length === 0 ? (
							<TableRow>
								<TableCell colSpan={5} sx={{ py: 3, color: 'text.secondary' }}>
									No costs yet. Track materials, labor, or subcontractors here.
								</TableCell>
							</TableRow>
						) : (
							rows.map((row) => {
								const isSaving = savingIds.has(row._key);
								return (
									<TableRow key={row._key}>
										<TableCell>
											<TextField
												value={row.description || ''}
												onChange={(e) =>
													updateRow(row._key, { description: e.target.value })
												}
												onBlur={() => saveRow(row._key, { quiet: true })}
												placeholder="Cost description"
												fullWidth
												size="small"
												disabled={isSaving}
											/>
										</TableCell>
										<TableCell align="right">
											<TextField
												value={row.quantity}
												onChange={(e) =>
													updateRow(row._key, { quantity: e.target.value })
												}
												onBlur={() => saveRow(row._key, { quiet: true })}
												size="small"
												type="number"
												inputProps={{ min: 1, step: 1 }}
												disabled={isSaving}
												sx={{ width: 72 }}
											/>
										</TableCell>
										<TableCell align="right">
											<TextField
												value={row.unit_cost}
												onChange={(e) =>
													updateRow(row._key, { unit_cost: e.target.value })
												}
												onBlur={() => saveRow(row._key, { quiet: true })}
												size="small"
												disabled={isSaving}
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">$</InputAdornment>
													),
												}}
												sx={{ width: 110 }}
											/>
										</TableCell>
										<TableCell align="right">
											{formatLineItemCurrency(costTotal(row))}
										</TableCell>
										<TableCell align="center">
											<IconButton
												size="small"
												color="error"
												onClick={() => handleDeleteRow(row)}
												disabled={isSaving}
												aria-label="Delete cost"
											>
												<DeleteIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					mt: 1.5,
				}}
			>
				<Button
					variant="outlined"
					color="primary"
					startIcon={<AddIcon />}
					onClick={() => {
						const draft = createDraftCost();
						setRows((prev) => {
							const next = [...prev, draft];
							rowsRef.current = next;
							return next;
						});
					}}
					size="small"
				>
					Add Cost
				</Button>
				<Typography variant="subtitle1" fontWeight={700}>
					Costs total: {formatLineItemCurrency(totalAmount)}
				</Typography>
			</Box>
		</Box>
	);
};

export default JobCostsEditor;
